/**
 * Phase A · Shared Sold-Out List formatter.
 *
 * Single source of plain-text formatting logic used by:
 *   • Admin Inventory → "Copy Sold-Out Stickers" button.
 *   • Public /order-missing → "LIVE SOLD-OUT STICKERS" section.
 *
 * Contract
 * --------
 *   groupSoldOutCodes(codes)
 *     → [ { prefix: "MEX", numbers: ["13","18"] }, … ]
 *   formatSoldOutText(codes, { emptyMessage })
 *     → plain-text payload ready for clipboard / DOM
 *
 * Rules (per Phase A brief):
 *   · Group by sticker prefix (MEX, RSA, BRA, FWC, CC, …).
 *   · Preserve official album order — the input is expected to already
 *     be sorted server-side via `album_sort_key`. This module never
 *     reorders groups; it only preserves the first-appearance order.
 *   · Numeric sort WITHIN a group — natural, not lexicographic.
 *     ARG-2 stays before ARG-10.
 *   · Preserve FWC00 verbatim (never `Number()`-coerce the digit half).
 *   · Deduplicate codes (a code appearing twice becomes one entry).
 *   · Compact input accepted: `BRA10`, `ARG-17`, `FWC-00`, `CC 4`.
 *   · Coca-Cola: canonical prefix `CC` (never `CCA`, never merged with
 *     the CC- codes as a country row).
 *   · Plain-text output: NO tabs, NO bullets, NO decorative separators.
 *   · Output line shape: `PREFIX: 4, 9` — comma-space between numbers.
 *   · Empty input serialises to the caller-provided `emptyMessage`,
 *     defaulting to "All tracked stickers are currently in stock."
 */

// ────────────────────────────────────────────────────────────────
// Code normalisation — accepts every legal shape and returns
// `{ prefix, numeric }` where:
//   • prefix   : uppercase letters (2..15 chars)
//   • numeric  : the digit portion AS-STRING (preserves FWC00).
//   • sortKey  : integer for within-group ordering (parseInt).
// Malformed codes return null so the formatter can skip them.
// ────────────────────────────────────────────────────────────────
const CODE_RE = /^([A-Z]{2,15})-?\s*(\d+)$/;

export function parseSoldOutCode(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toUpperCase().replace(/\s+/g, "");
  const m = CODE_RE.exec(s);
  if (!m) return null;
  const prefix = m[1];
  const numeric = m[2];
  // FWC00 is a real sticker — its leading zeros MUST survive. We keep
  // the raw digit string for display and derive the sort integer
  // separately.
  const isFwcZero = prefix === "FWC" && /^0+$/.test(numeric);
  const displayNum = isFwcZero ? "00" : String(parseInt(numeric, 10));
  const sortKey = isFwcZero ? 0 : parseInt(numeric, 10);
  return { prefix, numeric: displayNum, sortKey };
}

// ────────────────────────────────────────────────────────────────
// Group codes preserving first-appearance order.
// Numeric ordering within a group is natural (2 before 10).
// Duplicates collapse.
// ────────────────────────────────────────────────────────────────
export function groupSoldOutCodes(codes) {
  const byPrefix = new Map();   // prefix -> { numbers: Set<string>, seenAt: int }
  let idx = 0;
  for (const raw of codes || []) {
    const parsed = parseSoldOutCode(raw);
    if (!parsed) continue;
    const { prefix, numeric } = parsed;
    if (!byPrefix.has(prefix)) {
      byPrefix.set(prefix, { numbers: new Map(), seenAt: idx++ });
    }
    // Map<sortKey, displayNumber> so we can dedupe AND sort naturally
    // without losing FWC00's zero-padded display form.
    byPrefix.get(prefix).numbers.set(parsed.sortKey, numeric);
  }
  return Array.from(byPrefix.entries())
    .sort((a, b) => a[1].seenAt - b[1].seenAt)  // first-appearance order
    .map(([prefix, v]) => ({
      prefix,
      numbers: Array.from(v.numbers.entries())
        .sort((a, b) => a[0] - b[0])            // natural numeric sort
        .map(([, display]) => display),
    }));
}

// ────────────────────────────────────────────────────────────────
// Plain-text serialisation — one prefix per line, no decoration.
// ────────────────────────────────────────────────────────────────
export const DEFAULT_EMPTY_MESSAGE =
  "All tracked stickers are currently in stock.";

export function formatSoldOutText(codes, { emptyMessage } = {}) {
  const groups = groupSoldOutCodes(codes);
  if (groups.length === 0) {
    return (emptyMessage == null ? DEFAULT_EMPTY_MESSAGE : emptyMessage);
  }
  return groups
    .map(g => `${g.prefix}: ${g.numbers.join(", ")}`)
    .join("\n");
}

// Convenience for callers that need both the structured groups AND
// the plain-text payload — avoids re-parsing on both consumers.
export function buildSoldOutPayload(codes, opts = {}) {
  const groups = groupSoldOutCodes(codes);
  return {
    groups,
    text: groups.length === 0
      ? (opts.emptyMessage == null ? DEFAULT_EMPTY_MESSAGE : opts.emptyMessage)
      : groups.map(g => `${g.prefix}: ${g.numbers.join(", ")}`).join("\n"),
    isEmpty: groups.length === 0,
    totalCodes: groups.reduce((n, g) => n + g.numbers.length, 0),
  };
}
